import { useContext, useState } from "react";
import BryntumContext from "@/react/context/bryntumContext";
// @ts-expect-error - userPreferences is dynamically set in bryntumContext
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import { publishContextCards, publishFieldAdvisorCards } from "@/network/network";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { BoardPreferences } from "@/bryntum-lib/models/BoardPreferences";
import { toast } from "sonner";
import { InspectionType, AdvisorType } from "../constants/settingsStructure";
import { getPublishKey } from "../utils/promptUtils";
import { useBoardPermissions } from "./useBoardPermissions";

/**
 * Configuration for publishing prompt card changes.
 * Uses discriminated union pattern for type-safe handling of different prompt types.
 *
 * The 'mode' field acts as the discriminator, determining which properties are available.
 *
 * @example Publishing context cards
 * ```typescript
 * const config: PublishConfig = {
 *   mode: 'context',
 *   inspectionType: 'workmanship'
 * };
 * ```
 *
 * @example Publishing field advisor cards
 * ```typescript
 * const config: PublishConfig = {
 *   mode: 'field-advisor',
 *   advisorType: 'analyze'
 * };
 * ```
 */
export type PublishConfig =
  | { mode: 'context'; inspectionType: InspectionType }
  | { mode: 'field-advisor'; advisorType: AdvisorType };

// Unified hook for publishing all prompt card types
export const usePublishPromptCards = (config: PublishConfig) => {
  const { selectedBoard } = useContext(BryntumContext);
  const [isPublishing, setIsPublishing] = useState(false);

  // Get publish key based on config - used to track if publish button should appear
  const publishKey = config.mode === 'context'
    ? getPublishKey(config.inspectionType, undefined)
    : getPublishKey(undefined, config.advisorType);

  useSwivuStore({ storeId: STORE_KEYS.boardPreferencesStore, subscriberFn: ({ record }: { record: BoardPreferences }) => record.key === PERSISTENCE_STATE_KEYS[publishKey] });

  // Use shared permission hook
  const { canWrite } = useBoardPermissions();

  // Check board preference to see if there are unpublished changes
  // @ts-expect-error - userPreferences is dynamically set in bryntumContext
  const hasDraftChanges = userPreferences.getSetting(PERSISTENCE_STATE_KEYS[publishKey], "false", true) === "true";

  // Publish all draft cards
  const handlePublish = async () => {
    if (!canWrite) {
      toast.error("You don't have permission to publish prompt cards");
      return;
    }

    // @ts-expect-error - selectedBoard.record type not fully defined
    if (!selectedBoard?.record?.id) return;

    // @ts-expect-error - selectedBoard.record type not fully defined
    const boardId = selectedBoard.record.id;

    setIsPublishing(true);
    try {
      // Call appropriate publish endpoint based on mode
      if (config.mode === 'context') {
        await publishContextCards(boardId, config.inspectionType);
      } else {
        await publishFieldAdvisorCards(boardId, config.advisorType);
      }

      // Clear the publish indicator after successful publish
      // @ts-expect-error - userPreferences is dynamically set in bryntumContext
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS[publishKey], "false", true);

      // Show success notification
      toast.success("Changes published successfully");
    } catch (error) {
      console.error("Error publishing prompt cards:", error);
      toast.error("Failed to publish changes. Please try again.");
    } finally {
      setIsPublishing(false);
    }
  };

  return {
    hasDraftChanges,
    canPublish: canWrite && hasDraftChanges,
    handlePublish,
    isPublishing
  };
};
